import React, { useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';

type Dept = { id: number; company_id: number; code: string; name: string };

/**
 * Javna registracija — kreira korisnika u statusu "pending".
 * Superadministrator ga zatim odobrava u Administraciji (m.101).
 */
export function RegisterForm({ onBackToLogin }: { onBackToLogin: () => void }) {
    const [departments, setDepartments] = useState<Dept[]>([]);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [pwd, setPwd] = useState('');
    const [pwd2, setPwd2] = useState('');
    const [deptId, setDeptId] = useState<string>('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    useEffect(() => {
        api<{ departments: Dept[] }>('/register/departments', null)
            .then((r) => {
                setDepartments(r.departments);
                // ADMS default — prva HQ poslovnica (šifra 1001+) ili prva u popisu.
                const adms = r.departments.find((d) => /^10\d\d$/.test(d.code)) ?? r.departments[0];
                if (adms) setDeptId(String(adms.id));
            })
            .catch(() => setError('Ne mogu učitati popis poslovnica.'));
    }, []);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setError('');
        if (pwd !== pwd2) {
            setError('Lozinke se ne podudaraju.');
            return;
        }
        if (pwd.length < 10) {
            setError('Lozinka mora imati barem 10 znakova.');
            return;
        }
        if (!deptId) {
            setError('Odaberi poslovnicu.');
            return;
        }
        setBusy(true);
        try {
            await mutate('/register', null, 'POST', {
                name,
                email,
                password: pwd,
                primary_department_id: Number(deptId),
            });
            setDone(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Registracija nije uspjela.');
        } finally {
            setBusy(false);
        }
    }

    if (done) {
        return (
            <div className="card auth-card">
                <p className="eyebrow">Registracija</p>
                <h2>Hvala! 🎉</h2>
                <p className="lead">
                    Tvoja registracija je zaprimljena. Račun će biti aktivan nakon što ga
                    administrator odobri. Javit ćemo se na <strong>{email}</strong>.
                </p>
                <button type="button" onClick={onBackToLogin}>Natrag na prijavu</button>
            </div>
        );
    }

    // HQ poslovnice (šifre 1001+) idu na vrh — backend ih već sortira; ovdje samo grupiramo vizualno.
    const hq = departments.filter((d) => /^10\d\d$/.test(d.code));
    const rest = departments.filter((d) => !/^10\d\d$/.test(d.code));

    return (
        <form className="card auth-card auth-card-wide" onSubmit={submit}>
            <p className="eyebrow">Novi korisnik</p>
            <h2>Registracija</h2>
            <label>Ime i prezime<input value={name} onChange={(e) => setName(e.target.value)} required /></label>
            <label>Email<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required /></label>
            <label>
                Poslovnica
                <select value={deptId} onChange={(e) => setDeptId(e.target.value)} required>
                    <option value="" disabled>— odaberi —</option>
                    {hq.length > 0 ? (
                        <optgroup label="Uprava / službe">
                            {hq.map((d) => (
                                <option key={d.id} value={d.id}>{d.code} · {d.name}</option>
                            ))}
                        </optgroup>
                    ) : null}
                    {rest.length > 0 ? (
                        <optgroup label="Poslovnice">
                            {rest.map((d) => (
                                <option key={d.id} value={d.id}>{d.code} · {d.name}</option>
                            ))}
                        </optgroup>
                    ) : null}
                </select>
            </label>
            <label>Lozinka<input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} autoComplete="new-password" required /></label>
            <label>Ponovi lozinku<input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} autoComplete="new-password" required /></label>
            <button type="submit" disabled={busy}>{busy ? 'Šaljem…' : 'Registriraj se'}</button>
            <button type="button" className="secondary" onClick={onBackToLogin} style={{ marginTop: 8 }}>
                Već imaš račun? Prijavi se
            </button>
            <p className="error">{error}</p>
        </form>
    );
}
